import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Star, MessageCircle } from "lucide-react";
import { MaskedLines, FadeUp, SectionHeading } from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import FAQ from "@/components/FAQ";
import { POLITICAL_PACKAGES, CREATIVE_PACKAGES, ADDONS } from "@/data/packages";
import { CONTACT } from "@/data/services";
import useSeo from "@/hooks/useSeo";

const waLink = (p) => `${CONTACT.whatsapp.split("?")[0]}?text=${encodeURIComponent(`Namaste Anuj ji, I am interested in the ${p.name} package (${p.price} ${p.unit}). Please share details.`)}`;

const TABS = [
  { id: "political", label: "Political Campaigns", hindi: "चुनाव", data: POLITICAL_PACKAGES },
  { id: "creative", label: "Reels, Brand & Content", hindi: "क्रिएटिव", data: CREATIVE_PACKAGES },
];

function PackageCard({ p, i }) {
  return (
    <FadeUp delay={i * 0.08}>
      <TiltCard intensity={6} testId={`package-${p.id}`}>
        <div
          className={`group relative flex h-full flex-col rounded-2xl border p-8 transition-colors duration-500 ${
            p.highlight ? "border-brand/60 bg-gradient-to-b from-brand/15 via-coal to-coal shadow-[0_30px_80px_-40px_rgba(255,61,138,0.5)]" : "border-white/10 bg-coal/60 hover:border-saffron/40"
          }`}
        >
          {p.highlight && (
            <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white">
              <Star className="h-3 w-3" /> Most chosen
            </span>
          )}
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{p.level}</p>
              <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-paper">{p.name}</h3>
            </div>
            <span className="font-hindi text-lg text-saffron">{p.hindi}</span>
          </div>
          <div className="mt-6 flex items-end gap-2">
            <p className="font-display text-5xl font-bold tracking-tight text-paper">{p.price}</p>
            <p className="pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.unit}</p>
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-saffron">{p.duration}</p>
          <ul className="mt-8 flex-1 space-y-3">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                {f}
              </li>
            ))}
          </ul>
          <a
            href={waLink(p)}
            target="_blank"
            rel="noreferrer"
            data-testid={`package-whatsapp-${p.id}`}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/40 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-ink"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp for {p.name}
          </a>
          <Link
            to={`/contact?package=${encodeURIComponent(p.name)}`}
            data-testid={`package-cta-${p.id}`}
            className={`group/btn mt-3 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
              p.highlight ? "btn-ribbon text-white" : "border border-white/20 text-paper hover:border-saffron hover:text-saffron"
            }`}
          >
            Enquire for {p.name}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </Link>
        </div>
      </TiltCard>
    </FadeUp>
  );
}

export default function Packages() {
  useSeo("Packages & Pricing", "Transparent packages for Vidhan Sabha, Lok Sabha and Nagar Nigam campaigns, plus reels editing and brand launch plans. Prices in ₹, GST extra.");
  const [tab, setTab] = useState("political");
  const current = TABS.find((t) => t.id === tab);

  return (
    <div data-testid="packages-page" className="mx-auto max-w-[1440px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron">Packages — पैकेज</p>
      <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
        <MaskedLines inView={false} delay={0.1} lines={["Premium plans.", <span key="h" className="font-script normal-case italic text-brand">Honest prices.</span>]} />
      </h1>
      <FadeUp delay={0.3}>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
          Every package is a starting point — we scope the final plan to your seat, your date and your ground
          reality. GST extra. Election packages are billed monthly with a 30-day advance.
        </p>
      </FadeUp>

      <FadeUp delay={0.35}>
        <div className="mt-12 inline-flex rounded-full border border-white/10 bg-coal/60 p-1" data-testid="packages-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`packages-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative rounded-full px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t.id ? "text-white" : "text-zinc-400 hover:text-paper"
              }`}
            >
              {tab === t.id && <motion.span layoutId="pkg-tab" className="absolute inset-0 rounded-full bg-brand" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative">{t.label} <span className="font-hindi normal-case tracking-normal">· {t.hindi}</span></span>
            </button>
          ))}
        </div>
      </FadeUp>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.4 }}
          className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3"
          data-testid={`packages-grid-${tab}`}
        >
          {current.data.map((p, i) => (
            <PackageCard key={p.id} p={p} i={i} />
          ))}
        </motion.div>
      </AnimatePresence>

      <div className="mt-24 lg:mt-32">
        <SectionHeading overline="Add-ons — अतिरिक्त" lines={["Pick what", "you need."]} testId="addons-heading" />
        <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="addons-grid">
          {ADDONS.map((a) => (
            <div key={a.name} className="group flex items-center justify-between gap-4 bg-ink px-6 py-6 transition-colors duration-300 hover:bg-coal">
              <p className="text-sm text-zinc-300">{a.name}</p>
              <p className="font-display text-xl font-bold text-saffron">{a.price}</p>
            </div>
          ))}
        </div>
      </div>

      <FAQ className="mt-24 lg:mt-32" />

      <FadeUp className="mt-24 lg:mt-32">
        <div className="relative overflow-hidden rounded-2xl border border-brand/25 bg-gradient-to-br from-brand/10 via-coal to-ocean/10 p-10 lg:p-16">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div>
              <p className="font-hindi text-2xl text-saffron">सीट बताइए, तारीख बताइए।</p>
              <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl">
                We'll send a custom proposal within 48 hours.
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="packages-cta-button"
              className="group inline-flex items-center gap-2 rounded-full btn-ribbon px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 "
            >
              Request proposal
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </FadeUp>
    </div>
  );
}
